import React, { useState, lazy } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircleIcon, ChevronDownIcon, ArrowRightIcon, XCircleIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { SEOHead } from '../components/SEOHead';
const faqs = [{
  question: 'Are there long-term contracts?',
  answer: 'No. All our packages are month-to-month. We believe in earning your business every month, not locking you in. You can cancel anytime with 30 days notice.'
}, {
  question: 'Is the ad budget included in the monthly fee?',
  answer: "No, your ad spend is separate from our management fee. This gives you full control over your budget. We'll recommend a starting budget based on your market and goals."
}, {
  question: 'How often do I get reports?',
  answer: 'All clients receive monthly performance reports. Full Automation Suite clients also get monthly strategy calls to review results and plan next steps.'
}, {
  question: 'What kind of support do I get?',
  answer: 'All clients get email and chat support during business hours. Never Miss A Lead and Full Automation Suite clients get priority response times.'
}, {
  question: 'How quickly can I get started?',
  answer: 'Most clients are fully set up within 5-7 business days. We handle all the technical setup so you can focus on running your business.'
}, {
  question: 'Do I need to be tech-savvy?',
  answer: "Not at all. We handle everything technical. You'll get a simple dashboard and mobile app to manage your leads and appointments."
}];
const FAQItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-200 last:border-0" data-id="element-731">
      <button className="w-full py-6 flex items-center justify-between text-left focus:outline-none" onClick={() => setIsOpen(!isOpen)} data-id="element-732">
        <span className="text-lg font-semibold text-gray-900" data-id="element-733">{question}</span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.2
      }} className="flex-shrink-0 ml-4 text-viridia" data-id="element-734">
          <ChevronDownIcon className="w-5 h-5" data-id="element-735" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-736">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-737">
            <p className="pb-6 text-gray-600 leading-relaxed" data-id="element-738">{answer}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export function PricingPage() {
  return <div className="bg-viridia-black min-h-screen pt-24" data-id="element-739">
      <SEOHead title="Pricing | Marketing Packages for Local Businesses | Viridia Analytics Phoenix" description="Transparent, month-to-month pricing for ad campaign management, marketing automations, and web design. No long-term contracts. Packages starting for Phoenix local service businesses." canonical="/pricing" keywords="marketing agency pricing, ad management pricing Phoenix, marketing automation cost, web design pricing, local business marketing packages" data-id="element-740" />
      {/* HERO SECTION */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center" data-id="element-741">
        <ScrollReveal data-id="element-742">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 text-viridia text-sm font-semibold tracking-wide uppercase mb-6 border border-viridia/20" data-id="element-743">
            CRM & AUTOMATION
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6 tracking-tight" data-id="element-744">
            Automation Systems That{' '}
            <span className="text-viridia" data-id="element-745">Run Your Business</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto" data-id="element-746">
            Month-to-month systems that capture leads, automate follow-up, and
            keep your business organized — so you can focus on the work.
          </p>
        </ScrollReveal>
      </section>

      {/* PRICING CARDS SECTION */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-747">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center" data-id="element-748">
          {/* Card 1: Business Essentials */}
          <ScrollReveal delay={0.1} data-id="element-749">
            <div className="bg-viridia-surface rounded-2xl p-8 border border-gray-800 hover:shadow-2xl hover:shadow-viridia/5 transition-all duration-300 h-full flex flex-col" data-id="element-750">
              <div className="mb-8" data-id="element-751">
                <h3 className="text-2xl font-bold text-white mb-2" data-id="element-752">
                  Business Essentials
                </h3>
                <p className="text-gray-400 text-sm h-10" data-id="element-753">
                  The solid foundation: CRM, scheduling, and professional
                  billing.
                </p>
              </div>
              <div className="mb-8" data-id="element-754">
                <span className="text-5xl font-extrabold text-white" data-id="element-755">$497</span>
                <span className="text-gray-500 font-medium" data-id="element-756">/month</span>
              </div>
              <ul className="space-y-4 mb-8 flex-grow" data-id="element-757">
                {['CRM Setup & Contact Management', 'Online Booking / Calendar Integration', 'Professional Branded Invoicing & Revenue Tracking (PayPal / Square / Stripe sync)', 'QuickBooks & Local Billing Integration', 'Lead Capture Workflows', 'Yelp Auto-Responder (1 auto-response per new lead)', 'GHL Mobile App Access'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-758">
                    <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-759" />
                    <span className="text-gray-300 text-sm" data-id="element-760">{feature}</span>
                  </li>)}
              </ul>
              <Link to="/contact" className="w-full py-3 px-4 rounded-lg font-semibold text-center transition-all duration-300 border border-gray-600 text-white hover:bg-gray-800" data-id="element-761">
                Get Started
              </Link>
            </div>
          </ScrollReveal>

          {/* Card 2: Never Miss A Lead (MOST POPULAR) */}
          <ScrollReveal delay={0.2} data-id="element-762">
            <div className="bg-viridia-surface rounded-2xl p-8 border-2 border-viridia relative transform lg:scale-105 shadow-2xl shadow-viridia/10 h-full flex flex-col z-10" data-id="element-763">
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-viridia text-white px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider" data-id="element-764">
                Most Popular
              </div>
              <div className="mb-8 mt-2" data-id="element-765">
                <h3 className="text-2xl font-bold text-white mb-2" data-id="element-766">
                  Never Miss A Lead
                </h3>
                <p className="text-gray-400 text-sm h-10" data-id="element-767">
                  For busy contractors who lose jobs every time a call goes
                  unanswered.
                </p>
              </div>
              <div className="mb-8" data-id="element-768">
                <span className="text-5xl font-extrabold text-white" data-id="element-769">$897</span>
                <span className="text-gray-500 font-medium" data-id="element-770">/month</span>
              </div>
              <ul className="space-y-4 mb-8 flex-grow" data-id="element-771">
                <li className="flex items-start" data-id="element-772">
                  <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-773" />
                  <span className="text-white font-medium text-sm" data-id="element-774">
                    Everything in Business Essentials
                  </span>
                </li>
                {['Missed Call Text-Back Automation', 'AI Voice Receptionist (backup)', 'A2P Registration & Business Number Setup', 'Yelp AI Conversational Chatbot', 'Unified Communications Hub'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-775">
                    <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-776" />
                    <span className="text-gray-300 text-sm" data-id="element-777">{feature}</span>
                  </li>)}
              </ul>
              <Link to="/contact" className="w-full py-3 px-4 rounded-lg font-semibold text-center transition-all duration-300 bg-viridia hover:bg-viridia-dark text-white shadow-lg shadow-viridia/25" data-id="element-778">
                Get Started
              </Link>
            </div>
          </ScrollReveal>

          {/* Card 3: Full Automation Suite */}
          <ScrollReveal delay={0.3} data-id="element-779">
            <div className="bg-viridia-surface rounded-2xl p-8 border border-gray-800 hover:shadow-2xl hover:shadow-viridia/5 transition-all duration-300 h-full flex flex-col" data-id="element-780">
              <div className="mb-8" data-id="element-781">
                <h3 className="text-2xl font-bold text-white mb-2" data-id="element-782">
                  Full Automation Suite
                </h3>
                <p className="text-gray-400 text-sm h-10" data-id="element-783">
                  The full system. Your business runs on autopilot.
                </p>
              </div>
              <div className="mb-8" data-id="element-784">
                <span className="text-5xl font-extrabold text-white" data-id="element-785">
                  $1,497
                </span>
                <span className="text-gray-500 font-medium" data-id="element-786">/month</span>
              </div>
              <ul className="space-y-4 mb-8 flex-grow" data-id="element-787">
                <li className="flex items-start" data-id="element-788">
                  <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-789" />
                  <span className="text-white font-medium text-sm" data-id="element-790">
                    Everything in Never Miss A Lead
                  </span>
                </li>
                {['AI Conversational Chatbot (all applicable platforms & website)', 'Reputation Management', 'Review Automation', 'Pipeline Automation', 'Monthly Strategy Calls'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-791">
                    <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-792" />
                    <span className="text-gray-300 text-sm" data-id="element-793">{feature}</span>
                  </li>)}
              </ul>
              <Link to="/contact" className="w-full py-3 px-4 rounded-lg font-semibold text-center transition-all duration-300 border border-gray-600 text-white hover:bg-gray-800" data-id="element-794">
                Get Started
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* AD MANAGEMENT ADD-ON */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-795">
        <ScrollReveal direction="up" data-id="element-796">
          <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-797">
            <div data-id="element-798">
              <h3 className="text-2xl font-bold text-white mb-2" data-id="element-799">
                Need Ad Management Too?
              </h3>
              <p className="text-gray-400 max-w-2xl" data-id="element-800">
                Ad campaign management can be added to any package. Pricing
                depends on platforms, ad spend, and scope. Book a call to get a
                custom quote.
              </p>
            </div>
            <Link to="/contact" className="inline-flex items-center text-viridia hover:text-viridia-dark font-semibold transition-colors whitespace-nowrap" data-id="element-801">
              Get a Custom Ad Quote <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-802" />
            </Link>
          </div>
        </ScrollReveal>
      </section>

      {/* WEBSITE PRICING SECTION */}
      <section id="web-design" className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-803">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-804">
          <ScrollReveal data-id="element-805">
            <div className="text-center mb-16" data-id="element-806">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 text-viridia text-sm font-semibold tracking-wide uppercase mb-6 border border-viridia/20" data-id="element-807">
                WEB DESIGN & SEO
              </div>
              <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4" data-id="element-808">
                Website Packages
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto" data-id="element-809">
                High-converting, mobile-first websites built for local service
                businesses. One-time investment.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center" data-id="element-810">
            {/* Starter Site */}
            <ScrollReveal delay={0.1} data-id="element-811">
              <div className="bg-viridia-surface rounded-2xl p-8 border border-gray-800 hover:shadow-2xl hover:shadow-viridia/5 transition-all duration-300 h-full flex flex-col" data-id="element-812">
                <div className="mb-8" data-id="element-813">
                  <h3 className="text-2xl font-bold text-white mb-2" data-id="element-814">
                    Starter Site
                  </h3>
                  <p className="text-gray-400 text-sm h-10" data-id="element-815">
                    The essentials to get your business online and looking
                    professional.
                  </p>
                </div>
                <div className="mb-8" data-id="element-816">
                  <span className="text-4xl font-extrabold text-white" data-id="element-817">
                    $1,200–$1,800
                  </span>
                  <span className="text-gray-500 font-medium ml-2" data-id="element-818">
                    one-time
                  </span>
                </div>
                <ul className="space-y-4 mb-8 flex-grow" data-id="element-819">
                  {['3 pages (Home, Services, Contact)', 'Mobile responsive', 'Contact form', 'On-page SEO', 'Google Analytics setup'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-820">
                      <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-821" />
                      <span className="text-gray-300 text-sm" data-id="element-822">{feature}</span>
                    </li>)}
                </ul>
                <div className="mt-auto" data-id="element-823">
                  <Link to="/contact" className="block w-full py-3 px-4 rounded-lg font-semibold text-center transition-all duration-300 border border-gray-600 text-white hover:bg-gray-800 mb-3" data-id="element-824">
                    Get a Quote
                  </Link>
                  <p className="text-center text-gray-500 text-xs" data-id="element-825">
                    One-time fee. No hidden costs.
                  </p>
                </div>
              </div>
            </ScrollReveal>

            {/* Growth Site (MOST POPULAR) */}
            <ScrollReveal delay={0.2} data-id="element-826">
              <div className="bg-viridia-surface rounded-2xl p-8 border-2 border-viridia relative transform lg:scale-105 shadow-2xl shadow-viridia/10 h-full flex flex-col z-10" data-id="element-827">
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-viridia text-white px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider" data-id="element-828">
                  Most Popular
                </div>
                <div className="mb-8 mt-2" data-id="element-829">
                  <h3 className="text-2xl font-bold text-white mb-2" data-id="element-830">
                    Growth Site
                  </h3>
                  <p className="text-gray-400 text-sm h-10" data-id="element-831">
                    Built to convert traffic into leads and dominate local
                    search.
                  </p>
                </div>
                <div className="mb-8" data-id="element-832">
                  <span className="text-4xl font-extrabold text-white" data-id="element-833">
                    $2,500–$3,000
                  </span>
                  <span className="text-gray-500 font-medium ml-2" data-id="element-834">
                    one-time
                  </span>
                </div>
                <ul className="space-y-4 mb-8 flex-grow" data-id="element-835">
                  {['5–7 pages', 'Custom design', 'Service-specific pages', 'Review/trust section', 'Conversion CTAs', 'On-page SEO baked in'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-836">
                      <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-837" />
                      <span className="text-gray-300 text-sm" data-id="element-838">{feature}</span>
                    </li>)}
                </ul>
                <div className="mt-auto" data-id="element-839">
                  <Link to="/contact" className="block w-full py-3 px-4 rounded-lg font-semibold text-center transition-all duration-300 bg-viridia hover:bg-viridia-dark text-white shadow-lg shadow-viridia/25 mb-3" data-id="element-840">
                    Get a Quote
                  </Link>
                  <p className="text-center text-gray-400 text-xs" data-id="element-841">
                    One-time fee. No hidden costs.
                  </p>
                </div>
              </div>
            </ScrollReveal>

            {/* Premium Site */}
            <ScrollReveal delay={0.3} data-id="element-842">
              <div className="bg-viridia-surface rounded-2xl p-8 border border-gray-800 hover:shadow-2xl hover:shadow-viridia/5 transition-all duration-300 h-full flex flex-col" data-id="element-843">
                <div className="mb-8" data-id="element-844">
                  <h3 className="text-2xl font-bold text-white mb-2" data-id="element-845">
                    Premium Site
                  </h3>
                  <p className="text-gray-400 text-sm h-10" data-id="element-846">
                    The ultimate digital storefront for established businesses.
                  </p>
                </div>
                <div className="mb-8" data-id="element-847">
                  <span className="text-4xl font-extrabold text-white" data-id="element-848">
                    $4,000–$6,000
                  </span>
                  <span className="text-gray-500 font-medium ml-2" data-id="element-849">
                    one-time
                  </span>
                </div>
                <ul className="space-y-4 mb-8 flex-grow" data-id="element-850">
                  {['8–12 pages', 'Full custom design', 'Blog setup', 'Multiple service pages', 'Schema markup', 'Lead capture', 'Speed optimization'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-851">
                      <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-852" />
                      <span className="text-gray-300 text-sm" data-id="element-853">{feature}</span>
                    </li>)}
                </ul>
                <div className="mt-auto" data-id="element-854">
                  <Link to="/contact" className="block w-full py-3 px-4 rounded-lg font-semibold text-center transition-all duration-300 border border-gray-600 text-white hover:bg-gray-800 mb-3" data-id="element-855">
                    Get a Quote
                  </Link>
                  <p className="text-center text-gray-500 text-xs" data-id="element-856">
                    One-time fee. No hidden costs.
                  </p>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* LOCAL SEO ADD-ONS */}
      <section className="py-16 bg-viridia-black" data-id="element-857">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-858">
          <ScrollReveal data-id="element-859">
            <div className="text-center mb-12" data-id="element-860">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4" data-id="element-861">
                Local SEO Add-Ons
              </h3>
              <p className="text-gray-400 max-w-2xl mx-auto" data-id="element-862">
                Ongoing optimization to ensure your new website actually gets
                found by local customers.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-863">
            {/* Basic */}
            <ScrollReveal delay={0.1} data-id="element-864">
              <div className="bg-viridia-surface rounded-xl p-6 border border-gray-800 h-full flex flex-col" data-id="element-865">
                <h4 className="text-xl font-bold text-white mb-2" data-id="element-866">
                  Basic Local SEO
                </h4>
                <div className="mb-6" data-id="element-867">
                  <span className="text-2xl font-bold text-viridia" data-id="element-868">
                    $300–$500
                  </span>
                  <span className="text-gray-500 text-sm" data-id="element-869">/mo</span>
                </div>
                <ul className="space-y-3 mb-6 flex-grow" data-id="element-870">
                  {['GBP optimization', 'Citations', 'Monthly report'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-871">
                        <CheckCircleIcon className="w-4 h-4 text-viridia mr-2 flex-shrink-0 mt-1" data-id="element-872" />
                        <span className="text-gray-300 text-sm" data-id="element-873">{feature}</span>
                      </li>)}
                </ul>
              </div>
            </ScrollReveal>

            {/* Standard */}
            <ScrollReveal delay={0.2} data-id="element-874">
              <div className="bg-viridia-surface rounded-xl p-6 border border-gray-800 h-full flex flex-col" data-id="element-875">
                <h4 className="text-xl font-bold text-white mb-2" data-id="element-876">
                  Standard Local SEO
                </h4>
                <div className="mb-6" data-id="element-877">
                  <span className="text-2xl font-bold text-viridia" data-id="element-878">
                    $500–$800
                  </span>
                  <span className="text-gray-500 text-sm" data-id="element-879">/mo</span>
                </div>
                <ul className="space-y-3 mb-6 flex-grow" data-id="element-880">
                  <li className="flex items-start" data-id="element-881">
                    <CheckCircleIcon className="w-4 h-4 text-viridia mr-2 flex-shrink-0 mt-1" data-id="element-882" />
                    <span className="text-white font-medium text-sm" data-id="element-883">
                      Everything in Basic
                    </span>
                  </li>
                  {['On-page SEO', 'Schema markup', 'Review management'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-884">
                        <CheckCircleIcon className="w-4 h-4 text-viridia mr-2 flex-shrink-0 mt-1" data-id="element-885" />
                        <span className="text-gray-300 text-sm" data-id="element-886">{feature}</span>
                      </li>)}
                </ul>
              </div>
            </ScrollReveal>

            {/* Full */}
            <ScrollReveal delay={0.3} data-id="element-887">
              <div className="bg-viridia-surface rounded-xl p-6 border border-gray-800 h-full flex flex-col" data-id="element-888">
                <h4 className="text-xl font-bold text-white mb-2" data-id="element-889">
                  Full Local SEO
                </h4>
                <div className="mb-6" data-id="element-890">
                  <span className="text-2xl font-bold text-viridia" data-id="element-891">
                    $800–$1,200
                  </span>
                  <span className="text-gray-500 text-sm" data-id="element-892">/mo</span>
                </div>
                <ul className="space-y-3 mb-6 flex-grow" data-id="element-893">
                  <li className="flex items-start" data-id="element-894">
                    <CheckCircleIcon className="w-4 h-4 text-viridia mr-2 flex-shrink-0 mt-1" data-id="element-895" />
                    <span className="text-white font-medium text-sm" data-id="element-896">
                      Everything in Standard
                    </span>
                  </li>
                  {['Citation building', 'GBP posts & content', 'City pages', 'Competitor tracking'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-897">
                      <CheckCircleIcon className="w-4 h-4 text-viridia mr-2 flex-shrink-0 mt-1" data-id="element-898" />
                      <span className="text-gray-300 text-sm" data-id="element-899">{feature}</span>
                    </li>)}
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* HUBSPOT SEO CERTIFICATION */}
      <section className="py-16 bg-viridia-black" data-id="element-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-901">
          <ScrollReveal data-id="element-902">
            <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-center gap-8 md:gap-12" data-id="element-903">
              <div className="flex-shrink-0 w-full md:w-auto flex justify-center" data-id="element-904">
                <div className="relative group" data-id="element-905">
                  <div className="absolute -inset-1 bg-viridia/20 rounded-xl blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-906"></div>
                  <img src="https://cdn.magicpatterns.com/uploads/it67T9N3RYnBZ32NVuR62H/SEOcertified.png" alt="HubSpot Academy SEO Certification — Kevin Rogers" loading="lazy" className="relative w-64 md:w-72 rounded-lg shadow-xl border border-gray-700" data-id="element-907" />
                </div>
              </div>
              <div className="text-center md:text-left" data-id="element-908">
                <div className="flex items-center justify-center md:justify-start gap-3 mb-4" data-id="element-909">
                  <svg viewBox="0 0 512 512" className="w-8 h-8" aria-label="HubSpot logo" data-id="element-910">
                    <path fill="#FF7A59" d="M380.48 256c0-19.2-9.6-36.48-24.96-47.04V161.6c0-7.68-6.24-13.92-13.92-13.92h-.96c-7.68 0-13.92 6.24-13.92 13.92v40.32c-5.28-1.92-10.88-3.2-16.64-3.52V136c0-17.28-14.08-31.36-31.36-31.36-17.28 0-31.36 14.08-31.36 31.36v62.4c-5.76.32-11.36 1.6-16.64 3.52V161.6c0-7.68-6.24-13.92-13.92-13.92h-.96c-7.68 0-13.92 6.24-13.92 13.92v47.36C186.72 219.52 177.12 236.8 177.12 256c0 29.44 19.52 54.4 46.24 62.72l-24.64 24.64c-3.84 3.84-3.84 10.08 0 13.92 1.92 1.92 4.48 2.88 6.96 2.88s5.04-.96 6.96-2.88l31.04-31.04c5.28 1.6 10.88 2.56 16.64 2.88v44.48c0 17.28 14.08 31.36 31.36 31.36 17.28 0 31.36-14.08 31.36-31.36v-44.48c5.76-.32 11.36-1.28 16.64-2.88l31.04 31.04c1.92 1.92 4.48 2.88 6.96 2.88s5.04-.96 6.96-2.88c3.84-3.84 3.84-10.08 0-13.92l-24.64-24.64C360.96 310.4 380.48 285.44 380.48 256zM278.72 296.96c-22.56 0-40.96-18.4-40.96-40.96s18.4-40.96 40.96-40.96 40.96 18.4 40.96 40.96-18.4 40.96-40.96 40.96z" data-id="element-911" />
                  </svg>
                  <span className="text-sm font-bold text-[#FF7A59] tracking-wide uppercase" data-id="element-912">
                    HubSpot Academy
                  </span>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-3" data-id="element-913">
                  SEO Certified
                </h3>
                <p className="text-gray-400 leading-relaxed" data-id="element-914">
                  Our team is HubSpot SEO Certified — trained and tested on
                  technical SEO, on-page optimization, link building, and search
                  strategy. Not guesswork. Proven methodology.
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* FREELANCER COMPARISON */}
      <section className="py-20 bg-viridia-black border-t border-gray-800" data-id="element-915">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-916">
          <ScrollReveal data-id="element-917">
            <div className="text-center mb-12" data-id="element-918">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-919">
                Why Not Just Hire a Freelancer?
              </h2>
              <p className="text-lg text-gray-400" data-id="element-920">
                You get what you pay for. We build systems that generate
                revenue, not just pretty brochures.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-921">
            <ScrollReveal delay={0.1} data-id="element-922">
              <div className="bg-viridia-surface/50 rounded-2xl p-8 border border-red-900/30 h-full" data-id="element-923">
                <h3 className="text-xl font-bold text-gray-300 mb-6 flex items-center" data-id="element-924">
                  <span className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center mr-3" data-id="element-925">
                    <XCircleIcon className="w-5 h-5 text-red-400" data-id="element-926" />
                  </span>
                  The Average Freelancer
                </h3>
                <ul className="space-y-4" data-id="element-927">
                  {['Cheap, bloated templates', 'No marketing or conversion strategy', 'Zero accountability for results', 'Disappears after launch'].map((item, i) => <li key={i} className="flex items-start" data-id="element-928">
                      <XCircleIcon className="w-5 h-5 text-red-400 mr-3 flex-shrink-0 mt-0.5 opacity-70" data-id="element-929" />
                      <span className="text-gray-400" data-id="element-930">{item}</span>
                    </li>)}
                </ul>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-931">
              <div className="bg-viridia-surface rounded-2xl p-8 border border-viridia/30 h-full shadow-lg shadow-viridia/5" data-id="element-932">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center" data-id="element-933">
                  <span className="w-8 h-8 rounded-full bg-viridia/10 flex items-center justify-center mr-3" data-id="element-934">
                    <CheckCircleIcon className="w-5 h-5 text-viridia" data-id="element-935" />
                  </span>
                  Viridia Analytics
                </h3>
                <ul className="space-y-4" data-id="element-936">
                  {['Conversion-focused, custom builds', 'Deep local SEO knowledge', 'Ongoing support and optimization', 'Built specifically for your market'].map((item, i) => <li key={i} className="flex items-start" data-id="element-937">
                      <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-938" />
                      <span className="text-gray-200 font-medium" data-id="element-939">{item}</span>
                    </li>)}
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 bg-viridia-light text-gray-900" data-id="element-940">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-941">
          <ScrollReveal data-id="element-942">
            <div className="text-center mb-16" data-id="element-943">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-944">
                Frequently Asked Questions
              </h2>
              <p className="text-gray-600" data-id="element-945">
                Everything you need to know about our packages and billing.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.2} data-id="element-946">
            <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 p-6 md:p-8 border border-gray-100" data-id="element-947">
              {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-948" />)}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-gradient-to-br from-viridia to-viridia-dark relative overflow-hidden" data-id="element-949">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay" data-id="element-950"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-951">
          <ScrollReveal data-id="element-952">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-953">
              Not Sure Which Package Is Right?
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto" data-id="element-954">
              Book a free call and we'll recommend the perfect plan based on
              your business size and goals.
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-lg bg-white text-viridia-dark hover:bg-gray-50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1" data-id="element-955">
              Book Your Free Consultation
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}